import { forwardRef, type ComponentRef } from "react";

import { useWindowDimensions } from "@luce/core";

import { cn, platformProps } from "../../lib";
import { markPendingAware } from "../../lib/pending";
import { Pressable } from "../../primitives";
import {
  buttonIconColor,
  buttonSpinnerColorFor,
  buttonSpinnerSize,
  buttonTypographyColor,
  buttonTypographyVariant,
  buttonVariants,
} from "../button";
import { buttonHitSlop, buttonIconPx } from "../button/lib";
import { Icon } from "../icons";
import { Spinner } from "../spinner";
import { Typography } from "../typography";
import {
  fabButtonSizeToken,
  fabIconOnlyBoxClass,
  fabIconOnlyHitSlop,
  fabIconOnlyIconPx,
  fabSuccessIconClass,
  getFabButtonAccessibilityLabel,
  getFabButtonAccessibilityState,
} from "./lib";
import type { FabButtonProps } from "./types";

/**
 * Floating action button: a white, shadowed pill/circle.
 *
 * Built on `Pressable` reusing the shared `buttonVariants` (`neutral` surface) and
 * `button/lib` color/size maps — same pattern as `IconButton` — then layering the
 * bespoke bits a FAB needs: a drop shadow, and `overflow-visible` so it isn't
 * clipped (the shared base sets `overflow-hidden`).
 *
 * Content mode follows `label` — omit it for an icon-only (round) FAB, or set
 * it for a pill with an optional `leadingIcon` / `trailingIcon`.
 */
export const FabButton = forwardRef<ComponentRef<typeof Pressable>, FabButtonProps>(
  (
    {
      label,
      leadingIcon,
      trailingIcon,
      icon,
      leadingIconWeight,
      trailingIconWeight,
      iconWeight,
      variant = "neutral",
      size = "large",
      loading = false,
      className,
      testID,
      ...pressableProps
    },
    ref,
  ) => {
    const {
      onClick,
      disabled = false,
      accessibilityLabel,
      accessibilityState,
      hitSlop,
      pending = false,
      ...restPressableProps
    } = pressableProps;

    const isIconOnly = !label;
    const iconOnlyName = icon ?? leadingIcon ?? trailingIcon;
    const sizeToken = fabButtonSizeToken[size];
    const { isDesktop } = useWindowDimensions();
    const iconPx = (isIconOnly ? fabIconOnlyIconPx : buttonIconPx)[sizeToken][
      isDesktop ? "desktop" : "mobile"
    ];

    const accessibilityContext = {
      accessibilityLabel,
      accessibilityState,
      label,
      icon: iconOnlyName,
      loading,
      disabled,
    };
    const resolvedAccessibilityLabel = getFabButtonAccessibilityLabel(accessibilityContext);
    const mergedAccessibilityState = getFabButtonAccessibilityState(accessibilityContext);

    // One neutral look (split row 93), with a green icon on an icon-only Success
    // FAB (the designer, 2026-10-29). Figma draws no labelled Success FAB
    const iconColorClass = disabled
      ? buttonIconColor.neutral.disabled
      : variant === "success" && isIconOnly
        ? fabSuccessIconClass
        : buttonIconColor.neutral.default;

    const renderContent = () => {
      if (loading) {
        return (
          <Spinner
            size={buttonSpinnerSize[sizeToken]}
            color={buttonSpinnerColorFor("neutral", disabled)}
            decorative
            className="shrink-0"
          />
        );
      }

      if (isIconOnly) {
        return iconOnlyName ? (
          <Icon
            name={iconOnlyName}
            size={iconPx}
            weight={iconWeight}
            className={cn("shrink-0", iconColorClass)}
          />
        ) : null;
      }

      return (
        <>
          {leadingIcon && (
            <Icon
              name={leadingIcon}
              size={iconPx}
              weight={leadingIconWeight}
              className={cn("shrink-0", iconColorClass)}
            />
          )}
          <Typography
            variant={buttonTypographyVariant[sizeToken]}
            color={
              disabled
                ? buttonTypographyColor.neutral.disabled
                : buttonTypographyColor.neutral.default
            }
            whiteSpace="nowrap"
            className="max-w-full shrink truncate"
          >
            {label}
          </Typography>
          {trailingIcon && (
            <Icon
              name={trailingIcon}
              size={iconPx}
              weight={trailingIconWeight}
              className={cn("shrink-0", iconColorClass)}
            />
          )}
        </>
      );
    };

    return (
      <Pressable
        ref={ref}
        {...restPressableProps}
        {...platformProps({ testID, slot: "fab-button" })}
        accessibilityRole="button"
        accessibilityLabel={resolvedAccessibilityLabel}
        accessibilityState={mergedAccessibilityState}
        disabled={disabled}
        hitSlop={hitSlop ?? (isIconOnly ? fabIconOnlyHitSlop[sizeToken] : buttonHitSlop[sizeToken])}
        // Loading ignores every activation, not only onClick
        pending={pending || loading}
        onClick={onClick}
        className={cn(
          buttonVariants({ variant: "neutral", size: sizeToken, iconOnly: isIconOnly, loading }),
          // An icon-only FAB is bigger than IconButton (replaces its `cta` box)
          isIconOnly && fabIconOnlyBoxClass[sizeToken],
          // FAB surface: borderless white + Figma's FAB shadow, at rest, hovered and
          // loading (split row 103), and un-clip so the shadow shows. Pressing
          // lowers it. Disabled fills #f1f4f9, so the shape stays visible without
          // its shadow.
          "shadow-fab active:shadow-fab-pressed disabled:bg-luce-button-disabled-ctaDisabled overflow-visible border-0 transition-all disabled:shadow-none",
          className,
        )}
      >
        {renderContent()}
      </Pressable>
    );
  },
);

FabButton.displayName = "FabButton";
markPendingAware(FabButton);
